import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { addQuery, QueryVal } from 'Helpers/Query';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { FACET_PLAN_VIEW_SIDEBAR_SUMMARY } from 'Views/Page/Content/Templates/WhatIfTemplate/Components/Sidebar/consts';

const SUMMARY_SIDEBAR_TAB_QP = 'summarySidebarTab';

const SummarySidebarContentContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';
const SET_ACTIVE_SIDEBAR_TAB = 'set-active-sidebar-tab';

const init = (sidebarTabs) => {
  return {
    isInitializing: true,
    initializingError: null,
    facetPlanOutcomeSummaryViewCollection: null,
    activeSidebarTab: QueryVal(SUMMARY_SIDEBAR_TAB_QP) || sidebarTabs[0].value,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        facetPlanOutcomeSummaryViewCollection:
          action.facetPlanOutcomeSummaryViewCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case SET_ACTIVE_SIDEBAR_TAB: {
      return {
        ...state,
        activeSidebarTab: action.activeSidebarTab,
      };
    }
    default: {
      throw new Error(
        `Unhandled SummarySidebarContentContext action type: ${action.type}`,
      );
    }
  }
};

const SummarySidebarContentProvider = ({ children }) => {
  const {
    pageContentResource,
    pageContentCollection,
  } = usePageContentContext();
  const { loadModalCollectionByLink } = useModalContext();
  const { displayErrorMessage } = useSnackbarContext();

  const [summaryTabRelationshipData] = pageContentResource
    .attr('sidebarTabs')
    .resources()
    .list()
    .find(tab => tab.attr('key') === FACET_PLAN_VIEW_SIDEBAR_SUMMARY)
    .relationship(fr.relationships.tabData.name)
    .attr('data');

  const facetPlanInputCollection = pageContentCollection
    .included()
    .findResource(summaryTabRelationshipData)
    .attr('inputSection')
    .resources()
    .head();

  const balanceSheetTabData = facetPlanInputCollection
    .attr('facetPlanBalanceSheetCollection')
    .resources()
    .head()
    .attr('tabs')
    .resources()
    .list();

  const sidebarTabs = balanceSheetTabData.map(tab => ({
    key: tab.id(),
    label: tab.attr('total'),
    subLabel: tab.attr('title'),
    value: tab.resourceType,
    errorCount: tab.attr('errorCount'),
    warningCount: tab.attr('warningCount'),
  }));

  const [state, dispatch] = React.useReducer(reducer, sidebarTabs, init);

  const incomeData = facetPlanInputCollection
    .attr('incomeSections')
    .resources()
    .head()
    .attr('ClientIncomes')
    .resources()
    .list();

  const activeSidebarTabData = balanceSheetTabData
    .find(tab => tab.resourceType === state.activeSidebarTab)
    ?.attr('sections')
    .resources()
    .list() ?? [];

  const setActiveSidebarTab = React.useCallback(
    async activeSidebarTab => {
      // Set Active tab QP
      addQuery({
        [SUMMARY_SIDEBAR_TAB_QP]: activeSidebarTab,
      });

      // Set Active tab
      dispatch({
        type: SET_ACTIVE_SIDEBAR_TAB,
        activeSidebarTab,
      });
    },
    [state],
  );

  // ----------------- DIALOG ACTIONS  -----------------

  const loadModalCollection = React.useCallback(
    (collection, relationship) => {
      try {
        loadModalCollectionByLink(
          collection
            .relationship(fr.relationships[relationship].name)
            .link('related')
            .url(),
        );
      } catch (err) {
        displayErrorMessage(err, err.errors);
      }
    },
    [loadModalCollectionByLink, state],
  );

  const handleAddIncome = React.useCallback(
    incomeSection => {
      loadModalCollection(incomeSection, fr.relationships.addIncome.name);
    },
    [state],
  );

  const handleModifyIncome = React.useCallback(
    incomeItem => {
      const incomeItemSelfLink = incomeItem.link('self').url();
      loadModalCollectionByLink(incomeItemSelfLink);
    },
    [state],
  );

  const handleAddExpense = React.useCallback(() => {
    const expenseCollection = facetPlanInputCollection
      .attr('expenseSections')
      .resources()
      .head();

    loadModalCollection(expenseCollection, fr.relationships.addExpense.name);
  }, [facetPlanInputCollection, state]);

  const handleModifyExpense = React.useCallback(
    expenseItem => {
      const selfLink = expenseItem.link('self');
      if (selfLink) {
        const expenseItemSelfLink = selfLink.url();
        loadModalCollectionByLink(expenseItemSelfLink);
      }
    },
    [state],
  );

  const handleViewAccountDetail = React.useCallback(
    account => {
      loadModalCollection(account, fr.relationships.details.name);
    },
    [state],
  );

  const handleAddAccount = React.useCallback(
    resource => {
      loadModalCollectionByLink(resource.relationship(fr.relationships.addAccount.name).link('related').url());
    },
    [state],
  );

  const handleAddRealAsset = React.useCallback(
    resource => {
      loadModalCollectionByLink(resource.relationship(fr.relationships.addRealAsset.name).link('related').url());
    },
    [state],
  );

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      sidebarTabs,
      setActiveSidebarTab,
      activeSidebarTab: state.activeSidebarTab,
      activeSidebarTabData,
      incomeData,
      facetPlanInputCollection,
      handleViewAccountDetail,
      handleAddIncome,
      handleAddExpense,
      handleModifyIncome,
      handleModifyExpense,
      handleAddAccount,
      handleAddRealAsset,
    };
  }, [
    activeSidebarTabData,
    facetPlanInputCollection,
    handleAddAccount,
    handleAddRealAsset,
    handleAddExpense,
    handleAddIncome,
    handleModifyExpense,
    handleModifyIncome,
    handleViewAccountDetail,
    incomeData,
    setActiveSidebarTab,
    sidebarTabs,
    state,
  ]);

  return (
    <SummarySidebarContentContext.Provider value={memoizedValue}>
      {children}
    </SummarySidebarContentContext.Provider>
  );
};

// Use TabContent State Context
const useSummarySidebarContentContext = () => {
  const context = React.useContext(SummarySidebarContentContext);
  if (context === undefined) {
    throw new Error(
      'useSummarySidebarContentContext must be used within a SummarySidebarContentProvider',
    );
  }
  return context;
};

SummarySidebarContentProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export { SummarySidebarContentProvider, useSummarySidebarContentContext };
